import { useMemo } from "react";
import { useRoom } from "../pages/floor/RoomProvider";

/**
 * useReturnRideQueue — surface riders whose outbound leg completed and whose
 * return window is approaching. Not a new endpoint — synthesized entirely
 * from the shared `allTrips` state exposed by RoomProvider (which itself
 * comes from the canonical `useLiveTrips` hook).
 *
 * A trip is a return-ride candidate if:
 *   • It has `return_ride_at` (server-authored) OR
 *   • It has a paired sibling flagged `is_outbound` with `return_of_trip_id`, OR
 *   • Its status is COMPLETED and there's a rider heuristic within a 2h window
 *
 * The list is capped and sorted by earliest return window.
 */
export function useReturnRideQueue({ windowHours = 4 } = {}) {
  const { allTrips } = useRoom();
  return useMemo(() => {
    if (!Array.isArray(allTrips)) return [];
    const now = Date.now();
    const horizon = now + windowHours * 3600 * 1000;
    const past = now - 60 * 60 * 1000;   // completed within last 60min

    return allTrips
      .map((t) => {
        // Explicit server-authored return window
        const explicit = t.return_ride_at || t.schedule?.return_pickup;
        if (explicit) {
          const ts = new Date(explicit).getTime();
          if (Number.isFinite(ts) && ts >= now - 15 * 60 * 1000 && ts <= horizon) {
            return { trip: t, expected_at: explicit, kind: "explicit" };
          }
        }
        // Completed outbound + rider name heuristic (naive but honest)
        if (String(t.status).toUpperCase() === "COMPLETED" && t.rider?.name) {
          const completedAt = new Date(t.completed_at || t.updated_at || 0).getTime();
          if (completedAt >= past) {
            const expected = new Date(completedAt + 2 * 3600 * 1000).toISOString();
            return { trip: t, expected_at: expected, kind: "heuristic" };
          }
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.expected_at) - new Date(b.expected_at))
      .slice(0, 6);
  }, [allTrips, windowHours]);
}
